/**
 * TunisiaShip embeddable widget — main entry.
 *
 * Distributed as a single IIFE bundle exposing `window.TunisiaShip`.
 * Each render mounts inside a Shadow DOM so the host site CSS never
 * leaks into the widget (and vice-versa). See `docs/app-and-widget-spec.md`
 * § 6 for the full functional spec.
 *
 * Authentication model
 * --------------------
 * The widget hits the **public** widget endpoints
 * (``/v1/tunisiaship/widget/*``) which are anonymous from the browser
 * but enforce a per-deployment ``Origin`` allow-list server-side. No
 * resource token / API key is ever shipped to the browser.
 */
import { h, render } from 'preact';

import { apiBase } from './api';
import { Tracking } from './components/Tracking';
import { Rates } from './components/Rates';
import { Checkout } from './components/Checkout';
import { direction } from './messages';
import { SHADOW_CSS } from './styles';
import type {
  CheckoutOptions,
  ContainerSelector,
  GlobalConfig,
  RatesOptions,
  ThemeOptions,
  TrackingOptions,
  TunisiaShipGlobal,
  WidgetEvents,
  WidgetInstance,
  WidgetRate,
} from './types';

declare const __TS_VERSION__: string;

const VERSION = typeof __TS_VERSION__ === 'string' ? __TS_VERSION__ : '1.0.0';

let globalConfig: GlobalConfig = {};

function configure(cfg: GlobalConfig) {
  globalConfig = { ...globalConfig, ...cfg };
}

function resolveContainer(target: ContainerSelector): HTMLElement {
  if (typeof target === 'string') {
    const el = document.querySelector<HTMLElement>(target);
    if (!el) throw new Error(`TunisiaShip: container not found for selector "${target}"`);
    return el;
  }
  return target;
}

function applyTheme(host: HTMLElement, theme?: ThemeOptions) {
  if (!theme) return;
  if (theme.primary) host.style.setProperty('--ts-primary', theme.primary);
  if (theme.background) host.style.setProperty('--ts-bg', theme.background);
  if (theme.foreground) host.style.setProperty('--ts-fg', theme.foreground);
  if (theme.radius) host.style.setProperty('--ts-radius', theme.radius);
  if (theme.font) host.style.setProperty('--ts-font', theme.font);
  if (theme.mode === 'dark') host.setAttribute('data-mode', 'dark');
  else if (theme.mode === 'light') host.setAttribute('data-mode', 'light');
  else host.removeAttribute('data-mode');
}

interface MountResult {
  destroy: () => void;
  emit: (event: string, payload: unknown) => void;
  on: <K extends keyof WidgetEvents>(event: K, handler: (payload: WidgetEvents[K]) => void) => () => void;
}

function mount(host: HTMLElement, locale: string | undefined, theme: ThemeOptions | undefined): {
  shadow: ShadowRoot;
  root: HTMLElement;
  api: MountResult;
} {
  // If we re-mount on the same element (e.g. autoMount fires twice), reuse
  // any existing Shadow DOM rather than crashing on attachShadow().
  let shadow: ShadowRoot;
  if (host.shadowRoot) {
    shadow = host.shadowRoot;
    while (shadow.firstChild) shadow.removeChild(shadow.firstChild);
  } else {
    shadow = host.attachShadow
      ? host.attachShadow({ mode: 'open' })
      : (host as unknown as ShadowRoot);
  }
  const style = document.createElement('style');
  style.textContent = SHADOW_CSS;
  shadow.appendChild(style);
  const root = document.createElement('div');
  root.setAttribute('dir', direction((locale ?? 'fr') as 'fr' | 'ar' | 'en'));
  shadow.appendChild(root);
  applyTheme(host, theme);

  const handlers = new Map<string, Set<(payload: unknown) => void>>();
  const api: MountResult = {
    destroy: () => {
      render(null, root);
      try {
        host.shadowRoot?.replaceChildren();
      } catch {
        /* noop */
      }
    },
    emit: (event, payload) => {
      // 1. JS subscribers attached via instance.on(...)
      const set = handlers.get(event);
      if (set) for (const fn of set) fn(payload);
      // 2. CustomEvent dispatched on the host element so declarative
      //    consumers can ``el.addEventListener('tunisiaship:rate-selected', …)``.
      try {
        host.dispatchEvent(
          new CustomEvent(`tunisiaship:${event}`, {
            detail: payload,
            bubbles: true,
            composed: true,
          }),
        );
      } catch {
        /* old browser without CustomEvent constructor — silently ignore */
      }
    },
    on: (event, handler) => {
      const set = handlers.get(event) ?? new Set();
      set.add(handler as (payload: unknown) => void);
      handlers.set(event, set);
      return () => set.delete(handler as (payload: unknown) => void);
    },
  };
  return { shadow, root, api };
}

function tracking(opts: TrackingOptions): WidgetInstance {
  const host = resolveContainer(opts.container);
  const locale = opts.locale ?? globalConfig.locale ?? 'fr';
  const apiUrl = apiBase(globalConfig.apiUrl);
  const { root, api } = mount(host, locale, opts.theme ?? globalConfig.theme);
  render(
    h(Tracking, {
      ...opts,
      locale,
      apiUrl,
      emit: api.emit,
    }),
    root,
  );
  return { destroy: api.destroy, on: api.on };
}

function rates(opts: RatesOptions): WidgetInstance {
  const host = resolveContainer(opts.container);
  const locale = opts.locale ?? globalConfig.locale ?? 'fr';
  const apiUrl = apiBase(globalConfig.apiUrl);
  const { root, api } = mount(host, locale, opts.theme ?? globalConfig.theme);
  render(
    h(Rates, {
      ...opts,
      locale,
      apiUrl,
      emit: api.emit,
    }),
    root,
  );
  return { destroy: api.destroy, on: api.on };
}

function checkout(opts: CheckoutOptions): WidgetInstance {
  const host = resolveContainer(opts.container);
  const locale = opts.locale ?? globalConfig.locale ?? 'fr';
  const apiUrl = apiBase(globalConfig.apiUrl);
  const { root, api } = mount(host, locale, opts.theme ?? globalConfig.theme);
  render(
    h(Checkout, {
      ...opts,
      locale,
      apiUrl,
      emit: api.emit,
    }),
    root,
  );
  return { destroy: api.destroy, on: api.on };
}

function parseAddress(prefix: 'from' | 'to', el: HTMLElement) {
  return {
    postalCode: el.getAttribute(`data-${prefix}-postal`) ?? undefined,
    city: el.getAttribute(`data-${prefix}-city`) ?? undefined,
    countryCode: el.getAttribute(`data-${prefix}-country`) ?? 'TN',
  };
}

function parseParcels(el: HTMLElement) {
  const weight = parseFloat(el.getAttribute('data-weight') ?? '1') || 1;
  const length = parseFloat(el.getAttribute('data-length') ?? '');
  const width = parseFloat(el.getAttribute('data-width') ?? '');
  const height = parseFloat(el.getAttribute('data-height') ?? '');
  const parcel: { weight: number; length?: number; width?: number; height?: number } = { weight };
  if (Number.isFinite(length)) parcel.length = length;
  if (Number.isFinite(width)) parcel.width = width;
  if (Number.isFinite(height)) parcel.height = height;
  return [parcel];
}

function autoMount() {
  const declarative = document.querySelectorAll<HTMLElement>('[data-tunisiaship]');
  declarative.forEach((el) => {
    if ((el as HTMLElement & { __ts_mounted?: boolean }).__ts_mounted) return;
    const mode = el.getAttribute('data-tunisiaship');
    if (mode === 'tracking') {
      const trackingNumber = el.getAttribute('data-tracking-number') ?? '';
      const carrierName = el.getAttribute('data-carrier-name') ?? undefined;
      if (trackingNumber) {
        tracking({ container: el, trackingNumber, carrierName });
        (el as HTMLElement & { __ts_mounted?: boolean }).__ts_mounted = true;
      }
    } else if (mode === 'rates' || mode === 'checkout') {
      const opts: RatesOptions = {
        container: el,
        shipper: parseAddress('from', el),
        recipient: parseAddress('to', el),
        parcels: parseParcels(el),
      };
      if (mode === 'checkout') {
        checkout(opts);
      } else {
        rates(opts);
      }
      (el as HTMLElement & { __ts_mounted?: boolean }).__ts_mounted = true;
    }
  });
}

const cfg = (globalThis as unknown as { TunisiaShipConfig?: GlobalConfig }).TunisiaShipConfig;
if (cfg) configure(cfg);

const TunisiaShip: TunisiaShipGlobal = { version: VERSION, configure, tracking, rates, checkout };

(globalThis as unknown as { TunisiaShip: TunisiaShipGlobal }).TunisiaShip = TunisiaShip;

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
}

export type { WidgetRate };
export default TunisiaShip;
